/**
 * How a prompt card turns its connected cards into generation runs. Shared by
 * Studio (the canvas) and the API (server-side runs for agents); pure, no
 * server imports. Node and edge shapes match apps/studio's board-graph.
 */

export interface PlanNode {
	data: Record<string, unknown>;
	id: string;
	position: { x: number; y: number };
	type?: string;
}

export interface PlanEdge {
	source: string;
	target: string;
}

/**
 * How a prompt treats several connected generations: run once per image
 * ("each"), or merge them into one design ("combine").
 */
export type PromptMode = "each" | "combine";

export const LOCAL_MODEL = "local";
export const AUTO_MODEL = "auto";

const COMBINE_HINT_RE = /\b(combin|merg|join|mix|blend|fus|unif|mash)/i;

/**
 * Output aspect ratio for hosted runs. "auto" uses the prompt template's
 * default (generic: follow the input image; website 16:9; app 9:16).
 */
export const ASPECT_RATIOS = [
	"auto",
	"1:1",
	"16:9",
	"9:16",
	"4:3",
	"3:4",
	"3:2",
	"2:3",
	"21:9",
] as const;
export type AspectRatio = (typeof ASPECT_RATIOS)[number];

/** Model-agnostic quality tiers; each hosted model maps them to its options. */
export const QUALITY_TIERS = ["draft", "standard", "high", "max"] as const;
export type QualityTier = (typeof QUALITY_TIERS)[number];

export function isAspectRatio(value: unknown): value is AspectRatio {
	return ASPECT_RATIOS.includes(value as AspectRatio);
}

export function isQualityTier(value: unknown): value is QualityTier {
	return QUALITY_TIERS.includes(value as QualityTier);
}

interface PromptData {
	/** Hosted runs only; unset = the template's default shape. */
	aspect?: AspectRatio;
	mode?: PromptMode;
	model?: string;
	/** Hosted runs only; unset = the model's default quality. */
	quality?: QualityTier;
	text: string;
}

/** The user's choice, or a guess from the prompt text ("combine these…"). */
export function effectivePromptMode(data: PromptData): PromptMode {
	if (data.mode) {
		return data.mode;
	}
	return COMBINE_HINT_RE.test(data.text) ? "combine" : "each";
}

/** The prompt's model, or the default: local Codex on desktop, else Auto. */
export function effectivePromptModel(
	data: PromptData,
	options: { desktop: boolean; hostedAvailable: boolean }
): string {
	if (data.model && (data.model !== LOCAL_MODEL || options.desktop)) {
		return data.model;
	}
	if (options.desktop) {
		return LOCAL_MODEL;
	}
	return options.hostedAvailable ? AUTO_MODEL : LOCAL_MODEL;
}

/** Something wired into a prompt node: a pluck, an uploaded image or a generation. */
export type PromptInput =
	| { kind: "pluck"; pluckId: string }
	| { kind: "image"; uploadId: string }
	| { kind: "generation"; generationId: string };

function promptInputKey(input: PromptInput): string {
	switch (input.kind) {
		case "pluck":
			return `pluck:${input.pluckId}`;
		case "image":
			return `image:${input.uploadId}`;
		default:
			return `generation:${input.generationId}`;
	}
}

function toPromptInput(node: PlanNode): PromptInput | null {
	switch (node.type) {
		case "pluck":
			return { kind: "pluck", pluckId: String(node.data.pluckId) };
		case "image":
			return { kind: "image", uploadId: String(node.data.uploadId) };
		case "generation":
			return {
				kind: "generation",
				generationId: String(node.data.generationId),
			};
		default:
			return null;
	}
}

/**
 * Inputs wired into a prompt node, in the visual order of their nodes (top to
 * bottom, then left to right) so the numbered references in the prompt match
 * what the user sees.
 */
export function collectPromptInputs(
	nodes: PlanNode[],
	edges: PlanEdge[],
	promptNodeId: string
): PromptInput[] {
	const byId = new Map(nodes.map((n) => [n.id, n]));
	const sources = edges
		.filter((e) => e.target === promptNodeId)
		.map((e) => byId.get(e.source))
		.filter((n): n is PlanNode => n !== undefined)
		.sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
	const seen = new Set<string>();
	const inputs: PromptInput[] = [];
	for (const node of sources) {
		const input = toPromptInput(node);
		if (!input) {
			continue;
		}
		const key = promptInputKey(input);
		if (!seen.has(key)) {
			seen.add(key);
			inputs.push(input);
		}
	}
	return inputs;
}

export interface PlannedRun<T> {
	references: T[];
	revisesFirst: boolean;
}

/**
 * "each": every connected generation gets its own run with the prompt applied
 * to it (plucks and uploads are shared references). "combine": one run that
 * merges all inputs into a single design.
 */
export function planRuns<T extends { kind: PromptInput["kind"] }>(
	resolved: T[],
	data: PromptData
): PlannedRun<T>[] {
	const bases = resolved.filter((r) => r.kind === "generation");
	const shared = resolved.filter((r) => r.kind !== "generation");
	const combine = bases.length > 1 && effectivePromptMode(data) === "combine";
	if (bases.length === 0 || combine) {
		return [{ revisesFirst: false, references: resolved }];
	}
	return bases.map((base) => ({
		revisesFirst: true,
		references: [base, ...shared],
	}));
}
